<template>
  <div
    class="analysis-request-table-wrap analysisRequestWO"
  >
    <div
      class="analysis-table__container table-scroll-container pb-5"
    >
      <table>
        <thead class="freeze-table-header">
          <tr>
            <th>
              <div
                class="d-flex flex-row"
              >
                <div
                  v-for="(component, compIndex) in networkAnalysisData.components"
                  :key="component.id"
                  class="column-title p-0 mr-2 mb-4"
                  :class="compIndex === 0 ? 'first' : ''"
                >
                  <v-card
                    elevation="3"
                    :title="component.name"
                    style="z-index:5;"
                  >
                    <div
                      class="d-flex flex-row justify-space-between"
                      style="padding: 8px; max-height: 60px; min-height: 60px;"
                    >
                      <span
                        style="position: relative; text-align: start;"
                        class="text-ellipsis"
                      >
                        {{ component.name }}
                      </span>
                      <div
                        class="icon--container__component"
                      >
                        <v-icon
                          color="primary"
                          small
                          :disabled="isCompletedOrLocked"
                          @click="onClickAddComponentNote(component)"
                        >
                          {{ getIconName(component.id) }}
                        </v-icon>
                      </div>
                    </div>
                  </v-card>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="withBorders">
          <div>
            <div
              class="add--analysis__list"
            >
              <div
                class="analysis-label"
              >
                <span class="analysis-label-left">
                  {{ $t(`labWorkOrderTable.labels.analyses`) }}
                </span>
              </div>
              <div class="analysis-col--container">
                <tr
                  v-for="component in networkAnalysisData.components"
                  :key="component.id"
                  class="analysis-col add"
                >
                  <td
                    class="analysis-input"
                  >
                    <div class="add-btn__container">
                      <v-btn
                        color="secondary"
                        class="add-btn"
                        :disabled="isCompletedOrLocked"
                        @click="$emit('onAddAnalysisToComponent', { componentId: component.id })"
                      >
                        <v-icon left>
                          add
                        </v-icon>
                        {{ $t('sites.nodes.addAnalysis') }}
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </div>
            </div>
            <div
              v-for="analysisType in networkAnalysisData.analysisTypes"
              :key="analysisType.id"
              class="row-title--wrapper"
            >
              <tr
                class="td--row-title"
              >
                <td class="row-title">
                  <div
                    class="row-title--inner"
                  >
                    <div class="defaultW">
                      <v-img
                        v-if="analysisType.type === calculatedAnalysisType"
                        src="@/assets/images/fluent_math-formula-24-filled.svg"
                        @click="showFormulaForm(analysisType)"
                      />
                    </div>
                    <div>
                      {{ analysisType.name[userLang] || analysisType.name[teamMainLang] }}
                      <div class="noFont">
                        {{ getMeasureUnit(analysisType) }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <div class="analysis-col--container">
                <tr
                  v-for="(measure, measureIndex) in analysisType.measures"
                  :key="measureIndex"
                  class="analysis-col"
                >
                  <td class="analysis-input">
                    <div>
                      <AnalysisTableCell
                        v-if="measure.analysisId"
                        :workOrderId="workOrder.id"
                        :activityId="workOrder.activities.measuresAndSamplesCollect.id"
                        :analysisId="measure.analysisId"
                        :measure="getMeasure(measure.analysisId)"
                        :type="analysisType.type"
                        :isCompletedOrLocked="isCompletedOrLocked"
                        :tableId="tables[0].id"
                        :analysisTypeName="analysisType.name[userLang]"
                        :manualNormColor="measure.manualNormColor"
                        @reloadTable="getTables"
                        @reloadNetworkAnalysis="fetchData"
                      />
                    </div>
                  </td>
                </tr>
              </div>
              <!--        todo: Will do later when endpoint returns the analysis id-->
              <!--        <td class="action-buttons">-->
              <!--          <v-btn-->
              <!--            xSmall-->
              <!--            fab-->
              <!--            color="secondary"-->
              <!--            @click="onClickRemoveAnalysis(analysisType.id)"-->
              <!--          >-->
              <!--            <v-icon>-->
              <!--              mdi-close-->
              <!--            </v-icon>-->
              <!--          </v-btn>-->
              <!--        </td>-->
            </div>
          </div>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import completionStatus from '@/constants/workOrderStatusChoices';
import AddAutomaticSentenceFormVue from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm.vue';
import { CALCULATED_ANALYSIS_TYPE } from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import AnalysisTableCell from './AnalysisTableCell.vue';
import TreeFormulaForm from './TreeFormulaForm.vue';


export default {
    components: {
        AnalysisTableCell,
    },
    props: {
        networkId: {
            type: String,
            default: '',
        },
        onClickRemoveAnalysis: {
            type: Function,
            default: () => null,
        },
        analysisUpdates: {
            type: Number,
            default: 0,
        },
        tables: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            networkAnalysisData: {},
        };
    },
    computed: {
        ...mapGetters([
            'workOrder',
            'userLang',
            'teamMainLang',
        ]),
        activity() {
            return this.workOrder.activities.measuresAndSamplesCollect;
        },
        analyses() {
            return this.activity.tasks.analyses;
        },
        isCompletedOrLocked() {
            return this.workOrder.completionStatus === completionStatus.COMPLETED || !!this.workOrder?.lockData?.isLocked;
        },
        getNetwork() {
            return this.tables[0]?.networks?.find(network => network.id === this.networkId);
        },
        calculatedAnalysisType() {
            return CALCULATED_ANALYSIS_TYPE;
        },
    },
    watch: {
        analysisUpdates() {
            this.fetchData();
        },
    },
    created() {
        this.fetchData();
    },
    methods: {
        fetchData() {
            Promise.all([
                this.getNetworkAnalysis(this.activity.id, this.networkId),
                this.getTables(),
            ]);
        },
        async getNetworkAnalysis(activityId, networkId) {
            if (activityId && networkId) {
                const { data } = await this.$rq.getNetworkAnalysis(activityId, networkId);
                this.networkAnalysisData = data;
            } else {
                this.networkAnalysisData = { analysisTypes: [], components: [] };
            }
        },
        getMeasureUnit(analysisType) {
            if (analysisType.reportUnitOfMeasure && analysisType.reportUnitOfMeasure.length) {
                return analysisType.reportUnitOfMeasure;
            }
            if (analysisType.measureUnit) {
                return analysisType.measureUnit?.[this.userLang] ?? analysisType.measureUnit?.[this.teamMainLang];
            }
            return '';
        },
        getMeasure(analysisId) {
            if (analysisId) {
            // eslint-disable-next-line no-underscore-dangle
                const correspondingAnalyse = this.analyses?.find(analyse => analyse.id === analysisId || analyse._id === analysisId);
                // eslint-disable-next-line max-len
                return { ...correspondingAnalyse.measure, norm: correspondingAnalyse.norm, isPartOfMassBalance: correspondingAnalyse.isPartOfMassBalance } || {};
            }
            return {};
        },
        getComponent(componentId) {
            let component = {};
          // eslint-disable-next-line no-unused-expressions
          this.getNetwork?.components?.every(comp => {
              // eslint-disable-next-line no-underscore-dangle
              const _component = comp?.find(_comp => _comp.id === componentId);
              if (_component) {
                  component = _component;
                  return false;
              }
              return true;
          });
          return component;
        },
        hasComment(componentId) {
            const component = this.getComponent(componentId);
            return !!component?.note;
        },
        getIconName(componentId) {
            return this.hasComment(componentId) ? 'chat_bubble' : 'chat_bubble_outline';
        },
        onClickAddComponentNote(comp) {
            const component = this.getComponent(comp.id);
            const finalComp = isEmpty(component) ? comp : component;
            this.$root.$emit('showModal', {
                title: this.$t('actions.add.note'),
                component: Vue.component('AddAutomaticSentenceFormVue', AddAutomaticSentenceFormVue),
                payload: { measure: finalComp, isAnalysis: true },
                onConfirm: requestPayload => {
                    if (requestPayload !== null) this.updateComponentNote(finalComp.id, requestPayload);
                },
            });
        },
        async updateComponentNote(componentId, requestPayload) {
            await this.$rq.updateComponentNote(this.activity.id, this.tables[0].id, componentId, requestPayload);
            this.showMessage();
            this.getTables();
        },
        showMessage() {
            this.$toasted.global.toast_success({
                message: this.$t('msg.tableUpdated'),
            });
        },
        getTables() {
            this.$emit('getTables');
        },
        async showFormulaForm(analysisType) {
            const { analysisTypes } = this.networkAnalysisData;
            const { networks: tableNetworks } = this.tables[0];
            const calculatedAnalyses = analysisTypes.find(type => type.id === analysisType.id);
            const componentIds = calculatedAnalyses.measures.map(measure => measure.componentId).filter(Boolean);
            const promises = [];
            componentIds.forEach(componentId => {
                promises.push(this.$rq.getComponentConfigurations(componentId));
            });
            const componentsConfigurations = await Promise.all(promises);
            const configurations = componentsConfigurations.map(({ data }) => data?.configurations?.flat())
                .flat().filter(conf => conf.analysisType.id === analysisType.id);

            const formulaArray = [];
            configurations.forEach(config => {
                const formulaObj = { component: config.component, analysisType: config.analysisType };
                const variablesArray = [];
                config.calculatedAnalysisParams.variables.forEach(variable => {
                    const variableObj = { ...variable };

                    variableObj.analysisType = analysisTypes.find(at => at.id === variable.analysisType);

                    const foundNetwork = tableNetworks.find(tn => tn.id === variable.network)
                        ?.analysis.find(an => an.analysisType.id === variable.analysisType
                  && an.component.id === variable.component);
                    variableObj.value = foundNetwork?.measure?.value;
                    variablesArray.push(variableObj);
                    formulaObj.variables = variablesArray;
                });
                formulaArray.push(formulaObj);
            });
            this.$root.$emit('showModal', {
                title: `${this.$t('forms.labels.formula')} : ${analysisType.name[this.userLang] || analysisType.name[this.teamMainLang]}`,
                component: Vue.component('TreeFormulaForm', TreeFormulaForm),
                payload: { items: formulaArray },
                showConfirmButton: false,
            });
        },
    },
};
</script>

<style lang="scss">
  @import "@/assets/styles/utils/_variables.scss";
  @import '@/assets/styles/vuetify_variables.scss';
  @import '@/assets/styles/components/_analysisGrid.scss';

thead.freeze-table-header tr {
  background-color: white;
  width: 100%;
  z-index: 4;
  position: sticky;
  top: 0
}

.table-scroll-container {
  max-width: 100%;
  overflow-x: auto;
  margin-left: 45px;
  height: auto;
  max-height: 50em;
}

.analysisRequestWO {
  .td--row-title {
    position: sticky;
    left: 0;
    z-index: 3;
    background-color: white;
  }
  .analysis-label {
    position: sticky;
    left: 0;
    z-index: 3;
    height: 60px;
    display: flex;
    align-items: center;
    background-color: white;
      &-left {
        position: relative;
        left: 30px;
    }
  }
  .row-title--inner{
        column-gap: .5rem;
  }
  .defaultW{
        position: relative;
        top: -10px;
        width: 20px;
        height: 20px;
  }
  .noFont{
        font-weight: normal;
  }
}


</style>
